import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api, type PageResult } from '@/lib/api'

/**
 * i18n message catalog. Shapes follow `app/i18n/dto` exactly — `snake_case` on
 * the wire, converted nowhere, because these render as-is.
 *
 * `language_code` is **lowercase everywhere** (`zh-tw`, never `zh-TW`). It is
 * the same value `trending_item_locales.locale` carries and the same one the
 * `cwc-frontend/i18n/locales` directories are named after. Casing drift never
 * throws — it presents as "this language has no translations" for a language
 * that has them.
 *
 * Publishing lives at the bottom of this file. It exports the catalog to object
 * storage under D-1 — immutable versions, `manifest.json` as the commit point.
 * Nothing outside the console reads that output yet; see the note there.
 */

// ---------------------------------------------------------------- languages

export interface Language {
  id: number
  code: string
  display_code: string
  display_name: string
  llm_name: string
  is_baseline: boolean
  is_popular: boolean
  is_rtl: boolean
  sort_weight: number
  enabled: boolean
}

/**
 * `code` and `is_baseline` are absent deliberately. The code is a join key the
 * frontend locale directories already reference, so renaming it orphans them
 * silently; which language is the baseline is not an operator decision, and the
 * database rejects a second one regardless.
 */
export const languageSchema = z.object({
  // Optional, because the server derives it from the code when it is blank —
  // right for the twenty-three languages whose picker label IS their code. The
  // 8 is the column, and it is narrower than `code`'s 16 on purpose.
  display_code: z.string().trim().max(8, 'Keep it to 8 characters.'),
  display_name: z.string().trim().min(1, 'A display name is required.').max(64),
  llm_name: z.string().trim().max(64),
  is_popular: z.boolean(),
  is_rtl: z.boolean(),
  sort_weight: z.coerce.number().int('Whole numbers only.').min(0, 'Cannot be negative.'),
  enabled: z.boolean(),
})

export type LanguageInput = z.infer<typeof languageSchema>

/**
 * Lowercase, and the message says why: this value names a directory in
 * `cwc-frontend/i18n/locales` and is the same string `trending_item_locales`
 * stores. `zh-TW` never errors anywhere — it just finds no translations, which
 * reads as "this language has no content" rather than as a typo.
 */
const LANGUAGE_CODE_RE = /^[a-z]{2,3}(-[a-z0-9]+)*$/

export const languageCreateSchema = languageSchema.extend({
  code: z
    .string()
    .trim()
    .min(2, 'A language code is required.')
    .max(16)
    .regex(
      LANGUAGE_CODE_RE,
      'Lowercase BCP47, e.g. ja or zh-tw — it has to match the frontend locale folder exactly.',
    ),
})

export type LanguageCreateInput = z.infer<typeof languageCreateSchema>

// ------------------------------------------------------------------ modules

export interface Module {
  id: number
  code: string
  display_name: string
  /** `all`, or a comma-separated subset. See `scopeLabel` for why they differ. */
  platform_scope: string
  enabled: boolean
  sort_weight: number
}

/**
 * The platforms a module can be restricted to. `all` is absent on purpose — it
 * is not a fourth platform but the *unrestricted* sentinel, and the two are not
 * interchangeable. A module scoped `web,app,admin` still rejects a key filed
 * under platform `all`, because that key exports into every platform's files and
 * the module has to be unrestricted to take it (`platformInScope`, D-12).
 */
export const SCOPED_PLATFORMS = ['web', 'app', 'admin'] as const

/** `all` / `''` → unrestricted (empty list). Anything else → the named subset. */
export function parseScope(scope: string): string[] {
  const trimmed = scope.trim()
  if (trimmed === '' || trimmed === 'all') return []
  return trimmed
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function formatScope(platforms: string[]): string {
  return platforms.length === 0 ? 'all' : platforms.join(',')
}

export function scopeLabel(scope: string): string {
  const platforms = parseScope(scope)
  return platforms.length === 0 ? 'Any platform' : platforms.join(', ')
}

/**
 * A path segment in every published file, so it is immutable after creation.
 *
 * Exported because the mock layer enforces it too — it mirrors
 * `moduleCodeRe` in `app/i18n/service/common.go`, which did not exist until
 * 2026-08-21: the console rejected a malformed code and the API accepted one,
 * the same drift `KEY_RE` and `LANGUAGE_CODE_RE` were bitten by.
 */
export const MODULE_CODE_RE = /^[a-z][a-z0-9_-]*$/

/**
 * Names why a platform scope is invalid, or null when it is fine. Mirrors
 * `validatePlatformScope` in `app/i18n/service/common.go`.
 *
 * `all` is the unrestricted sentinel and is only valid on its own — `web,all`
 * reads as though it widens the module, while the server treats each comma part
 * as a literal platform name and would never match `all` against a real one, so
 * every key filed under it would be refused forever.
 */
export function platformScopeProblem(scope: string): string | null {
  const trimmed = scope.trim()
  if (trimmed === 'all') return null
  const bad = `invalid platform scope: "${scope}" is not one of web, app, admin (or the bare value "all")`
  for (const part of trimmed.split(',')) {
    const p = part.trim()
    if (p === '' || p === 'all' || !SCOPED_PLATFORMS.includes(p as (typeof SCOPED_PLATFORMS)[number])) {
      return bad
    }
  }
  return null
}

export const moduleSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'A module code is required.')
    .max(64, 'Keep it to 64 characters.')
    .regex(MODULE_CODE_RE, 'Lowercase letters, numbers, - and _ only — it becomes a filename.'),
  display_name: z.string().trim().min(1, 'A display name is required.').max(64),
  platform_scope: z.string(),
  enabled: z.boolean(),
  sort_weight: z.coerce.number().int('Whole numbers only.').min(0, 'Cannot be negative.'),
})

export type ModuleInput = z.infer<typeof moduleSchema>

// ----------------------------------------------------------------- messages

export const PLATFORMS = ['all', 'web', 'app', 'admin'] as const
export type Platform = (typeof PLATFORMS)[number]

/**
 * Derived at read time from `is_manual` / `needs_review` — never stored, so
 * there is no third representation to fall out of agreement with the columns.
 *
 * `needs_review` outranks `human`: a human translation whose English has since
 * moved is exactly the row an operator must find, and badging it "done" hides it
 * from the queue it belongs in.
 *
 * `failed` (R7b) is `missing` where the last machine attempt failed. Never behind
 * content — content always wins, so a human translation is never failed. A manual
 * row with no content is failed only when the failure is newer than its last
 * write: a forced retranslate whose provider call failed.
 */
export type TranslationState = 'human' | 'machine' | 'needs_review' | 'missing' | 'failed'

/** Why the last machine attempt failed. Set only while `status` is `failed`. */
export type FailureReason = 'provider_error' | 'empty_output' | 'placeholder_rejected' | 'write_error'

const FAILURE_TEXT: Record<FailureReason, string> = {
  provider_error: 'the translation service returned an error',
  empty_output: 'the translation service returned nothing',
  placeholder_rejected: 'the draft lost or changed a placeholder and was discarded',
  write_error: 'the draft could not be saved',
}

/** One line under a failed row in the editor. Unknown codes render as-is. */
export function failureText(reason: FailureReason | null): string {
  return reason ? (FAILURE_TEXT[reason] ?? reason) : 'the last attempt failed'
}

export interface TranslationStatus {
  language_code: string
  status: TranslationState
}

export interface MessageRow {
  id: number
  message_key: string
  platform: Platform
  module: string
  /**
   * `null` means no English row has ever existed for this key — a real state,
   * not a bug. `key_search_or_exchanges` is live in ja/ko/zh-tw with no English
   * at all. An empty string is different again: English deliberately blank.
   */
  baseline_content: string | null
  context_desc: string | null
  updated_by: string | null
  updated_at: string | null
  translations: TranslationStatus[]
}

export interface Translation {
  language_code: string
  display_name: string
  is_rtl: boolean
  content: string | null
  is_manual: boolean
  needs_review: boolean
  override_reason: string | null
  updated_by: string | null
  updated_at: string | null
  status: TranslationState
  /** Set only while `status` is `failed`; the provider's error text is never sent. */
  failure_reason: FailureReason | null
  failed_at: string | null
}

export interface MessageDetail {
  id: number
  message_key: string
  platform: Platform
  module: string
  baseline_content: string | null
  context_desc: string | null
  created_by: string | null
  updated_by: string | null
  created_at: string | null
  updated_at: string | null
  translations: Translation[]
}

export interface TranslateResult {
  /** What was QUEUED, not what succeeded — the work outlives the request. */
  queued: number
  languages: string[]
  skipped_manual: number
}

/**
 * Dot-separated segments, or a flat legacy key. Capitals and underscores are
 * allowed on purpose: the imported corpus is entirely `key_market_cap`-shaped
 * and some of those keys have 40+ call sites, so they cannot be renamed even
 * where they are misspelled.
 */
const KEY_RE = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/

export const messageSchema = z.object({
  message_key: z
    .string()
    .trim()
    .min(1, 'A message key is required.')
    .max(255, 'Keys are capped at 255 characters.')
    .regex(KEY_RE, 'Use letters, numbers, underscores and dots only (e.g. module.section.name).'),
  module: z.string().min(1, 'Pick a module.'),
  platform: z.enum(PLATFORMS),
  // Not `.trim()`: trailing whitespace is content here. `key_see_all` ends in a
  // space that concatenates with the next element, and trimming on save would
  // silently change live copy.
  baseline_content: z.string(),
  context_desc: z.string().max(255, 'Keep the context note under 255 characters.'),
})

export type MessageInput = z.infer<typeof messageSchema>

// ------------------------------------------------- placeholder validation

/**
 * Mirrors `ExtractPlaceholders` in `app/i18n/service/placeholder.go` byte for
 * byte. Keep the two identical: a client that accepts what the server rejects
 * produces a save that fails with no visible cause.
 *
 * The single-brace form allows inner whitespace, which PRD v2 §7.2 does not.
 * `key_banner_desc_1` — live today — reads
 * "The { categoryName } market cap today is { formattedMarketCapToday }, ", and
 * under the spec's stricter pattern it contains zero placeholders, so the
 * validator would wave through a translation that destroyed both.
 *
 * Keep this identical to placeholderRe in app/i18n/service/placeholder.go. The
 * two are not the same by construction and have drifted three times.
 *
 * First on `\s`: JavaScript's spans 25 characters and Go's covers 5, so
 * `{ name }` typed with a non-breaking space matched in the browser and vanished
 * on the server. The patch for that named U+00A0 and U+3000 and left the other 12
 * diverging, including U+202F, the narrow no-break space French typography uses
 * before punctuation. The patch for THAT matched the class by construction and
 * was still blind to a dot: `{user.name}` — which is how Vue i18n spells a nested
 * parameter, and this catalog is consumed by a Nuxt app — matched neither side,
 * and a string with zero placeholders on both sides passes validation trivially.
 *
 * So the single-brace inner class is now defined by what it EXCLUDES, and the only
 * exclusion is the brace itself. Every whitespace character and every name shape
 * is inside it by construction, which is the point: enumeration is what leaked
 * three times. Comparison stays byte-exact, so `{ name }` and `{name}` are still
 * different placeholders.
 *
 * The double-brace alternative must stay FIRST. Both engines take the
 * leftmost-first alternative, so that ordering is what makes `{{name}}` match as
 * one placeholder rather than as `{name}` found one character in.
 *
 * If you edit this line, run the Go tests in tests/app/i18n/placeholder_test.go —
 * TestExtractPlaceholdersCoversEveryJavaScriptWhitespaceChar enumerates all 25
 * whitespace characters and TestExtractPlaceholdersFindsNamesThatAreNotIdentifiers
 * covers the dotted and hyphenated forms.
 */
const PLACEHOLDER_RE = /\{\{[^}]+\}\}|\{[^{}]*\}|%[0-9]*\$?[sdvfx]|<\/?[a-zA-Z][^>]*>/g

export function extractPlaceholders(value: string): string[] {
  return (value.match(PLACEHOLDER_RE) ?? []).slice().sort()
}

export interface PlaceholderProblem {
  missing: string[]
  unexpected: string[]
  message: string
}

/**
 * Returns null when the translation preserves the baseline's placeholder set.
 *
 * Comparison is order-insensitive because Japanese and Arabic routinely need a
 * different argument order than English — rejecting that is the usual objection
 * to placeholder validation and it does not apply here. Duplicates count: a
 * baseline with two `%s` is not satisfied by a translation carrying one.
 *
 * An empty translation is not a problem. It means untranslated, which is a real
 * state — the key ships English under D-7.
 */
export function placeholderProblem(baseline: string, translation: string): PlaceholderProblem | null {
  if (!translation.trim()) return null

  const want = extractPlaceholders(baseline)
  const got = extractPlaceholders(translation)
  if (want.join('\u0000') === got.join('\u0000')) return null

  const difference = (a: string[], b: string[]) => {
    const remaining = new Map<string, number>()
    for (const item of b) remaining.set(item, (remaining.get(item) ?? 0) + 1)
    const out: string[] = []
    for (const item of a) {
      const count = remaining.get(item) ?? 0
      if (count > 0) remaining.set(item, count - 1)
      else out.push(item)
    }
    return out
  }

  const missing = difference(want, got)
  const unexpected = difference(got, want)
  const parts: string[] = []
  if (missing.length) parts.push(`missing ${missing.join(', ')}`)
  if (unexpected.length) parts.push(`unexpected ${unexpected.join(', ')}`)

  return {
    missing,
    unexpected,
    message: `Placeholder mismatch — ${parts.join(' · ')}. The app substitutes these at runtime, so a changed one renders as literal text.`,
  }
}

// ------------------------------------------------- placeholder form (R5)

/**
 * Mirrors `ValidatePlaceholderSyntax` and friends in
 * `app/i18n/service/placeholder.go`. The server is the authority; this exists so
 * the operator sees the problem inline instead of as a toast after Save. Keep the
 * two regexes, the platform list and the message identical.
 *
 * Web copy (platform `web`, or `all`, which publishes into the web tree too)
 * takes ONE placeholder form: an identifier in single braces, `{count}`.
 * `{ name }`, `{{name}}`, `%s`/`%1$s`, `{user.name}` and a stray brace are all
 * refused. `app` and `admin` keys are out of scope — the mobile app's own
 * formatting is not visible from here.
 */
const NAMED_PLACEHOLDER_RE = /^\{[A-Za-z_][A-Za-z0-9_]*\}$/
/** PLACEHOLDER_RE without the tag alternative: tags are not interpolation. */
const SYNTAX_TOKEN_RE = /\{\{[^}]+\}\}|\{[^{}]*\}|%[0-9]*\$?[sdvfx]/g

export const PLACEHOLDER_SYNTAX_PLATFORMS: readonly Platform[] = ['all', 'web']

export function placeholderSyntaxApplies(platform: Platform): boolean {
  return PLACEHOLDER_SYNTAX_PLATFORMS.includes(platform)
}

/** Returns null when every placeholder in `value` is the named `{name}` form. */
export function placeholderSyntaxProblem(value: string): string | null {
  const bad: string[] = []
  const seen = new Set<string>()
  for (const token of value.match(SYNTAX_TOKEN_RE) ?? []) {
    if (NAMED_PLACEHOLDER_RE.test(token) || seen.has(token)) continue
    seen.add(token)
    let described = `"${token}"`
    if (token.startsWith('{')) {
      const fixed = `{${token.replace(/^\{+|\}+$/g, '').trim()}}`
      if (NAMED_PLACEHOLDER_RE.test(fixed)) described += ` (write ${fixed})`
    }
    bad.push(described)
  }
  if (/[{}]/.test(value.replace(SYNTAX_TOKEN_RE, ''))) bad.push('an unmatched { or }')
  if (bad.length === 0) return null
  return `invalid placeholder syntax: ${bad.join(', ')} — write each placeholder as a name in single braces with no spaces, e.g. {count}`
}

/**
 * The translation half, relative to the English exactly as the server's is: a
 * translation of a legacy English template (key_banner_desc_1's
 * `{ categoryName }`) has to carry the legacy form to pass the mismatch check,
 * so the form rule stands aside for it until the English is fixed.
 */
export function translationSyntaxProblem(
  platform: Platform,
  baseline: string,
  translation: string,
): string | null {
  if (!placeholderSyntaxApplies(platform) || !translation.trim()) return null
  if (placeholderSyntaxProblem(baseline)) return null
  return placeholderSyntaxProblem(translation)
}

/**
 * `messageSchema` plus the placeholder-form rule on the English.
 *
 * `original` is the row being edited (absent on create). The rule fires when the
 * English changes, or when the key moves onto a web platform from one that is
 * not — the same condition `Update` applies — so a legacy key's context note or
 * module can still be saved without first rewriting its English.
 */
export function messageSchemaFor(original?: Pick<MessageDetail, 'baseline_content' | 'platform'>) {
  return messageSchema.superRefine((values, ctx) => {
    if (!placeholderSyntaxApplies(values.platform)) return
    const englishChanged = !original || values.baseline_content !== (original.baseline_content ?? '')
    if (!englishChanged && placeholderSyntaxApplies(original.platform)) return
    const problem = placeholderSyntaxProblem(values.baseline_content)
    if (problem) ctx.addIssue({ code: 'custom', path: ['baseline_content'], message: problem })
  })
}

// -------------------------------------------------------------------- hooks

const MESSAGES_KEY = 'i18n-messages'
const LANGUAGES_KEY = 'i18n-languages'
const MODULES_KEY = 'i18n-modules'

export interface MessageFilters {
  page?: number
  pageSize?: number
  keyword?: string
  module?: string
  platform?: string
  /**
   * Scopes `status` and `needsReview`. Both are ignored without it — except
   * `status: 'failed'`, which without a language means "in any language".
   */
  lang?: string
  status?: TranslationState
  needsReview?: boolean
}

export function useMessages(filters: MessageFilters = {}) {
  const {
    page = 1,
    pageSize = 20,
    keyword = '',
    module = '',
    platform = '',
    lang = '',
    status,
    needsReview = false,
  } = filters

  return useQuery({
    queryKey: [MESSAGES_KEY, page, pageSize, keyword, module, platform, lang, status ?? '', needsReview],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      // Search is server-side, and has to be: the list is paginated, so a
      // browser-side filter would report "no matches" for every key past the
      // page boundary — indistinguishable from the key not existing.
      if (keyword.trim()) params.set('keyword', keyword.trim())
      if (module) params.set('module', module)
      if (platform) params.set('platform', platform)
      if (lang) params.set('lang', lang)
      if (status && (lang || status === 'failed')) params.set('status', status)
      if (needsReview) params.set('needs_review', 'true')
      return api.get<PageResult<MessageRow>>(`/admin/i18n/messages?${params}`)
    },
  })
}

export function useMessageDetail(id: number | null) {
  return useQuery({
    queryKey: [MESSAGES_KEY, 'detail', id],
    queryFn: () => api.get<MessageDetail>(`/admin/i18n/messages/${id}`),
    enabled: id !== null,
  })
}

export function useLanguages() {
  return useQuery({
    queryKey: [LANGUAGES_KEY],
    // No `enabled=true`: the console shows disabled languages greyed rather
    // than hiding them, because a language vanishing from the screen when
    // somebody takes it offline reads as data loss.
    queryFn: () => api.get<Language[]>('/admin/i18n/languages'),
  })
}

/**
 * Defaults to enabled-only, because the common caller is the module dropdown and
 * a disabled module is not a valid filing choice — the backend rejects it with
 * "module is not in the module dictionary". The management screen passes
 * `false`, so it can show disabled rows greyed rather than making a module
 * vanish when someone takes it offline.
 */
export function useModules(enabledOnly = true) {
  return useQuery({
    queryKey: [MODULES_KEY, enabledOnly],
    queryFn: () =>
      api.get<Module[]>(`/admin/i18n/modules${enabledOnly ? '?enabled=true' : ''}`),
  })
}

export function useCreateModule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ModuleInput) => api.post<Module>('/admin/i18n/modules', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MODULES_KEY] }),
  })
}

/**
 * The whole row goes back, like the language endpoint — an omitted field would
 * be written as its zero value. `code` is not in the body at all: it is a path
 * segment in every published file, so renaming it would strand the old files
 * with nothing reporting an error.
 */
export function useSaveModule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ModuleInput }) =>
      api.put<Module>(`/admin/i18n/modules/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [MODULES_KEY] })
      // A module's display name shows on every catalog row.
      void queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })
    },
  })
}

/**
 * A new language starts with no translations at all, so every existing key reads
 * as untranslated for it — and under D-7 that means those keys ship the English
 * string. Adding a language is therefore instantly user-visible, and the backfill
 * (`translate-missing`) is a separate, explicit step.
 */
export function useCreateLanguage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: LanguageCreateInput) =>
      api.post<Language>('/admin/i18n/languages', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LANGUAGES_KEY] })
      // A new language adds a column to every row of the catalog.
      void queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })
    },
  })
}

export function useSaveLanguage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: LanguageInput }) =>
      api.put<Language>(`/admin/i18n/languages/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LANGUAGES_KEY] })
      // Enabling a language changes which columns the catalog renders.
      void queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] })
    },
  })
}

export function useCreateMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: MessageInput) => api.post<MessageDetail>('/admin/i18n/messages', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

/**
 * Sends **only the fields that changed**, and that is a contract requirement
 * rather than an optimisation.
 *
 * The backend implements D-11 by treating an omitted field as "do not touch".
 * A client that always sends every field would make a `context_desc` typo fix
 * indistinguishable from an English rewrite — and an English change clears every
 * machine translation under the key. Sending the whole form on every save would
 * quietly wipe the catalog one edit at a time.
 */
export function useUpdateMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, changes }: { id: number; changes: Partial<MessageInput> }) =>
      api.put<MessageDetail>(`/admin/i18n/messages/${id}`, changes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

export function useSaveTranslation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { message_key: string; language_code: string; content: string }) =>
      api.put<MessageDetail>('/admin/i18n/messages/details', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

/**
 * The escape hatch for a translation the placeholder check rejected. A separate
 * call rather than a flag, because the two are different acts: one is routine,
 * the other is a decision to ship something the validator believes is broken.
 * The reason is what makes that decision attributable six months later.
 */
export function useOverrideTranslation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      message_key: string
      language_code: string
      content: string
      reason: string
    }) => api.put<MessageDetail>('/admin/i18n/messages/details/override', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

/**
 * `lang` narrows the run to one language; omitting it regenerates every machine
 * draft under the key.
 *
 * The editor always passes one, because its Retranslate control sits inside a
 * single language's row. A button there that regenerated all of them would be
 * unrecoverable in the ordinary case — the other drafts are already gone by the
 * time anyone reads the toast.
 */
export function useRetranslate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, lang, force }: { id: number; lang?: string; force?: boolean }) => {
      // `force` regenerates a row a person wrote. The server refuses it without
      // a language, because forcing every language at once would discard every
      // human translation on the key from a single click.
      const params = new URLSearchParams()
      if (lang) params.set('lang', lang)
      if (force) params.set('force', 'true')
      const query = params.toString()
      return api.post<TranslateResult>(
        `/admin/i18n/messages/${id}/retranslate${query ? `?${query}` : ''}`,
      )
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

export function useTranslateMissing() {
  const queryClient = useQueryClient()
  return useMutation({
    // Both optional; omitted means the whole catalog. Scoping exists because
    // nothing reports progress on a backfill, so the only way to know one
    // worked is to keep it small enough to check.
    mutationFn: (scope?: { lang?: string; module?: string }) => {
      const params = new URLSearchParams()
      if (scope?.lang) params.set('lang', scope.lang)
      if (scope?.module) params.set('module', scope.module)
      const query = params.toString()
      return api.post<TranslateResult>(
        `/admin/i18n/messages/translate-missing${query ? `?${query}` : ''}`,
      )
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
  })
}

// --------------------------------------------------------------- publishing

/**
 * Publishing is D-1. Two properties of it drive this whole screen:
 *
 *  - **Every publish is immutable.** A publish writes a new `v{T}` directory
 *    and never touches an existing one, so a version that shipped can always be
 *    read back exactly as it shipped.
 *  - **`manifest.json` is the commit point.** Files in the bucket change
 *    nothing; the manifest naming them is what makes a version live. Rollback
 *    therefore writes no files — it moves a pointer.
 *
 * Which is why rollback is instant and safe, and why there is no version table
 * anywhere: the bucket carries the history.
 */
export interface PublishLanguageStatus {
  language_code: string
  display_name: string
  is_baseline: boolean
  /** Coverage right now, in the database. */
  translated_keys: number
  /** Empty until this language has been published at least once. */
  /**
   * What the MANIFEST serves — what a client fetching right now actually gets.
   *
   * Read from the manifest rather than from the newest publish-log row, because
   * the manifest is what decides it (D-2). The server used to answer this from
   * the log alone, and the two differ whenever the log is missing a row: the
   * manifest write is the commit point and the INSERT recording it comes after.
   * The console reported v…310 as live while the manifest served v…311, and
   * offered a rollback from the wrong one.
   */
  version: string
  /**
   * The newest version in the publish log. Equal to `version` under all normal
   * operation; different means the log and the manifest have diverged, which the
   * screen shows rather than picking one silently.
   */
  recorded_version: string
  published_at: string
  published_by: string
  published_keys: number
  /**
   * How many published keys carry the English string rather than a translation
   * (D-7). Every file holds every key, so `published_keys` alone cannot say how
   * much of it is really translated — this is the number that can.
   */
  filled_from_en: number
  /**
   * Keys changed since that publish. Coverage and drift answer different
   * questions and a language can be perfect on one and bad on the other —
   * 100% translated and entirely stale is the state nothing else here reveals.
   */
  drift: number
  /**
   * False until a RECORDED version exists that is not the live one — the question
   * the rollback endpoint actually answers. Always false for the baseline, which
   * cannot be rolled back on its own: D-7 baked its English into every other
   * language's file, so moving only its manifest entries leaves all of them
   * serving the string being withdrawn.
   */
  can_rollback: boolean
}

export interface PublishStatus {
  /** Keys with a usable English baseline — the denominator for coverage. */
  total_keys: number
  languages: PublishLanguageStatus[]
}

export interface PublishResult {
  language_code: string
  /** Unix seconds — the `v{T}` path segment (D-1). */
  version: string
  keys: number
  translated: number
  filled_from_en: number
  files: string[]
  published_at: string
  /** Echoes the request: the manifest this wrote maps this language to `version` under `urgent`. */
  urgent: boolean
}

const PUBLISH_KEY = 'i18n-publish'

const publishQuery = {
  queryKey: [PUBLISH_KEY],
  queryFn: () => api.get<PublishStatus>('/admin/i18n/publish/status'),
}

/** The whole payload — used for `total_keys`, the coverage denominator. */
export function usePublishStatus() {
  return useQuery(publishQuery)
}

/**
 * The rows, for DataTable, which takes a query rather than an array so it owns
 * loading, error and empty.
 *
 * Same query key as above, so React Query serves both from one cache entry and
 * one network call — `select` narrows without refetching. The alternative was
 * to hand DataTable a hand-built object wrapping an inline array, which the
 * component's contract exists to prevent.
 */
export function usePublishLanguages() {
  return useQuery({ ...publishQuery, select: (data: PublishStatus) => data.languages })
}

/**
 * `baseline` is its own route rather than a language code, because publishing
 * English is a different act: it is what every other language falls back to, so
 * it changes what an untranslated key renders as everywhere at once.
 */
/**
 * Publishing the baseline returns a different shape from publishing a language,
 * because it is a different operation: English cannot be published alone. Every
 * language whose live files carry English for untranslated keys is republished
 * with it, and `cascaded` is the only report of that having happened.
 */
export interface BaselinePublishResult {
  baseline: PublishResult
  cascaded: PublishResult[]
  // Cascade languages left as they were: every file they would publish already
  // matched the live one. They get no new version and no publish-log row, so
  // their Drift column counts the English change instead of resetting.
  unchanged?: string[]
  failed?: { language_code: string; error: string }[]
}

export function usePublish() {
  const queryClient = useQueryClient()
  return useMutation({
    /**
     * `urgent` marks, in the manifest, the languages this publish writes, so their
     * readers apply the new copy immediately instead of at their next navigation
     * (web R10). The next ordinary publish or rollback of a language clears its mark.
     */
    mutationFn: async ({
      code,
      isBaseline,
      urgent = false,
    }: {
      code: string
      isBaseline: boolean
      urgent?: boolean
    }) => {
      if (!isBaseline) {
        const result = await api.post<PublishResult>(`/admin/i18n/publish/${code}`, { urgent })
        return { baseline: result, cascaded: [] } satisfies BaselinePublishResult
      }
      return api.post<BaselinePublishResult>('/admin/i18n/publish/baseline', { urgent })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PUBLISH_KEY] }),
  })
}

export function useRollback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => api.post<PublishResult>(`/admin/i18n/publish/${code}/rollback`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PUBLISH_KEY] }),
  })
}

/**
 * One key a publish of a language would change — one of the rows its Drift
 * column counts (R2b). The previously published value is not available: it
 * lives only in the bucket.
 */
export interface PublishChange {
  message_key: string
  module: string
  platform: Platform
  /**
   * The key's own row was edited since the last publish: its English, name,
   * module, platform or context note. A note-only edit ships nothing and is
   * listed anyway, as Drift counts it, so this does not say the English moved.
   */
  english_changed: boolean
  translation_changed: boolean
  /** What the next publish ships. `null` on a translation: it ships `baseline_content`. */
  current_value: string | null
  baseline_content: string | null
  updated_at: string | null
  updated_by: string | null
}

/**
 * The first page of a language's unpublished changes, for the confirm dialog.
 * Only asked for a language that has been published — the server answers 2001
 * for one that has not, because every key would be "new".
 */
export function usePublishChanges(code: string | null, pageSize = 50) {
  return useQuery({
    queryKey: [PUBLISH_KEY, 'changes', code, pageSize],
    queryFn: () =>
      api.get<PageResult<PublishChange>>(
        `/admin/i18n/publish/${code}/changes?page=1&page_size=${pageSize}`,
      ),
    enabled: code !== null,
  })
}

/**
 * A short-lived link to one published object. Requested on demand and never
 * stored: it expires, so a saved one is a link that worked in testing and is
 * dead by the time anybody follows it.
 */
export function publishedFileURL(key: string) {
  return api.get<{ url: string }>(`/admin/i18n/publish/file?key=${encodeURIComponent(key)}`)
}
